// =============================================================================
// src/data/analytics-dashboard-query.ts — Query do dashboard GERADA por escopo.
//
// Saiu de analytics-live-query.ts em 2026-09-02 (arquivo no limite de 500
// linhas e a query virou funcao). Os 7 buckets do feed sao dados: alias +
// restricao propria + ordenacao no SQL; o escopo (analytics-scope-where) entra
// em TODOS. Novos no dashboard: sl_serving_v_mentions_daily_scoped (KPIs por
// escopo, migration 025) e sl_serving_v_ingestion_freshness (frescor real).
// =============================================================================

import { ANALYTICS_GRAPHQL_ROOTS } from './analytics-graphql-roots';
import {
  ANALYTICS_FEED_FIELDS_FRAGMENT,
  ANALYTICS_FEED_PLATFORM_LIMIT,
  ANALYTICS_FEED_RECENT_LIMIT,
  ANALYTICS_FEED_TOP_LIMIT,
  ANALYTICS_FEED_VIEWS_LIMIT,
  ANALYTICS_TOP_AUTHORS_LIMIT,
  type AnalyticsFeedBucketAlias,
} from './analytics-live-query';
import {
  ANALYTICS_DEFAULT_FEED_SCOPE,
  analyticsScopeWhere,
  type AnalyticsScopeWhereOptions,
} from './analytics-scope-where';

interface AnalyticsFeedBucketSpec {
  readonly alias: AnalyticsFeedBucketAlias;
  readonly where: string;
  readonly orderBy: string;
  readonly limit: number;
}

const TOPS_WINDOW = 'published_at: { _gte: $topsSinceTs }';

/** Buckets do feed: selecao multi-criterio ordenada NO SQL (decisao 2026-07-25). */
const ANALYTICS_FEED_BUCKET_SPECS: readonly AnalyticsFeedBucketSpec[] = [
  { alias: 'recentMentions', where: 'published_at: { _is_null: false }', orderBy: '{ published_at: desc }', limit: ANALYTICS_FEED_RECENT_LIMIT },
  { alias: 'topEngagementPosts', where: `content_kind: { _in: ["post"] }, ${TOPS_WINDOW}`, orderBy: '[{ engagement_score: desc }, { published_at: desc }]', limit: ANALYTICS_FEED_TOP_LIMIT },
  { alias: 'topLikedComments', where: `content_kind: { _in: ["comment", "reply"] }, ${TOPS_WINDOW}`, orderBy: '[{ likes_count: desc }, { published_at: desc }]', limit: ANALYTICS_FEED_TOP_LIMIT },
  { alias: 'topViewedContent', where: `views_count: { _gt: 0 }, ${TOPS_WINDOW}`, orderBy: '[{ views_count: desc }]', limit: ANALYTICS_FEED_VIEWS_LIMIT },
  { alias: 'tiktokTopContent', where: `platform: { _eq: "tiktok" }, ${TOPS_WINDOW}`, orderBy: '[{ engagement_score: desc }]', limit: ANALYTICS_FEED_PLATFORM_LIMIT },
  { alias: 'facebookTopContent', where: `platform: { _eq: "facebook" }, ${TOPS_WINDOW}`, orderBy: '[{ engagement_score: desc }]', limit: ANALYTICS_FEED_PLATFORM_LIMIT },
  { alias: 'xTopContent', where: `platform: { _eq: "x" }, ${TOPS_WINDOW}`, orderBy: '[{ engagement_score: desc }]', limit: ANALYTICS_FEED_PLATFORM_LIMIT },
];

export const ANALYTICS_DEFAULT_DASHBOARD_QUERY_OPTIONS: AnalyticsScopeWhereOptions = {
  scope: ANALYTICS_DEFAULT_FEED_SCOPE,
  showReactions: false,
};

function analyticsFeedBucketMarkup(bucket: AnalyticsFeedBucketSpec, scopeWhere: string): string {
  return `    ${bucket.alias}: ${ANALYTICS_GRAPHQL_ROOTS.mentionsFeed}(
      where: { client_id: { _eq: $clientId }, ${bucket.where}, ${scopeWhere} }
      order_by: ${bucket.orderBy}
      limit: ${bucket.limit}
    ) { ...AnalyticsFeedFields }`;
}

/**
 * Documento do dashboard para um escopo. A permissao anon impoe limit 5000 por
 * query; $sinceDate (120d) serve series e diaria por escopo (medido 2026-09-02:
 * 2.541 linhas em 120d), $authorSinceDate/$topsSinceTs seguem o periodo da UI.
 *
 * @example buildAnalyticsDashboardQuery({ scope: 'omar', showReactions: false })
 */
export function buildAnalyticsDashboardQuery(options: AnalyticsScopeWhereOptions): string {
  const scopeWhere = analyticsScopeWhere(options);
  const feedBuckets = ANALYTICS_FEED_BUCKET_SPECS.map((bucket) => analyticsFeedBucketMarkup(bucket, scopeWhere)).join('\n');
  return `
  query AnalyticsDashboard($clientId: uuid!, $sinceDate: date!, $authorSinceDate: date!, $topsSinceTs: timestamptz!) {
    ${ANALYTICS_GRAPHQL_ROOTS.mentionsDaily}(
      where: { client_id: { _eq: $clientId }, day: { _gte: $sinceDate } }
      order_by: { day: asc }
    ) {
      day
      platform
      content_kind
      mentions
      likes_total
      comments_total
      shares_total
      views_total
    }
    mentionsDailyScoped: ${ANALYTICS_GRAPHQL_ROOTS.mentionsDailyScoped}(
      where: { client_id: { _eq: $clientId }, day: { _gte: $sinceDate } }
      order_by: { day: asc }
    ) {
      day
      platform
      content_kind
      is_nucleo
      is_vip
      is_substantivo
      mentions
      likes_total
      comments_total
      shares_total
      views_total
    }
    ingestionFreshness: ${ANALYTICS_GRAPHQL_ROOTS.ingestionFreshness}(
      where: { client_id: { _eq: $clientId } }
    ) {
      platform
      last_collected_at
      last_published_at
      collected_24h
      collected_7d
    }
${feedBuckets}
    ${ANALYTICS_GRAPHQL_ROOTS.authorWindow}(
      where: { client_id: { _eq: $clientId }, platform: { _neq: "web" } }
      order_by: { mentions: desc }
      limit: ${ANALYTICS_TOP_AUTHORS_LIMIT}
    ) {
      author_handle
      author_name
      platform
      followers_current
      mentions
      engagement_score
    }
    ${ANALYTICS_GRAPHQL_ROOTS.newsSourceDaily}(
      where: { client_id: { _eq: $clientId }, day: { _gte: $authorSinceDate } }
      order_by: { day: desc }
    ) {
      day
      source_name
      mentions
      engagement_score
    }
    actorPlatformWindow: ${ANALYTICS_GRAPHQL_ROOTS.actorPlatformWindow}(
      where: { client_id: { _eq: $clientId } }
    ) {
      actor_slug
      actor_name
      actor_role
      platform
      mentions
    }
    watchWindow: ${ANALYTICS_GRAPHQL_ROOTS.watchWindow}(
      where: { client_id: { _eq: $clientId } }
      order_by: { mentions: desc }
    ) {
      watch_slug
      theme_label
      mentions
      interactions
    }
    regionWindow: ${ANALYTICS_GRAPHQL_ROOTS.regionWindow}(
      where: { client_id: { _eq: $clientId } }
      order_by: { mentions: desc }
      limit: 16
    ) {
      region_name
      region_kind
      uf
      mentions
    }
  }

  ${ANALYTICS_FEED_FIELDS_FRAGMENT}
`;
}

/** Documento do escopo padrao (Nucleo, sem reacoes) — contrato travado em teste. */
export const ANALYTICS_DASHBOARD_QUERY = buildAnalyticsDashboardQuery(ANALYTICS_DEFAULT_DASHBOARD_QUERY_OPTIONS);
